/**
 * AlreadyRegisteredModal — centered dialog shown when the OCR or final
 * submission detects that the owner / tax ID is already present in the
 * onboarding submissions table. Used by both the Documents step (early
 * detection from OCR) and the Review step (final guard before submit).
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, LogIn, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface AlreadyRegisteredModalProps {
  open: boolean;
  onClose: () => void;
}

const AlreadyRegisteredModal: React.FC<AlreadyRegisteredModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="already-registered-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="already-registered-title"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.cancel') as string}
              className="absolute top-3 end-3 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>

            <div className="flex flex-col items-center text-center">
              <span className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-4">
                <AlertTriangle size={28} />
              </span>
              <p
                id="already-registered-title"
                className="text-base sm:text-lg font-semibold text-gray-900 leading-relaxed mb-6"
              >
                {t('documents.errors.alreadyRegistered')}
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-2 w-full">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-haat-red-600 text-white text-sm font-medium hover:bg-haat-red-700 transition-colors"
                >
                  <LogIn size={14} />
                  {t('documents.actions.goToLogin')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default AlreadyRegisteredModal;
